import { useState, useEffect } from 'react';
import { Plus, Search, Users, AlertTriangle, DollarSign, TrendingUp } from 'lucide-react';
import useCustomerStore from '../../stores/useCustomerStore';
import CustomerCard from '../../components/customers/CustomerCard';
import CustomerModal from '../../components/customers/CustomerModal';
import Toast from '../../components/common/Toast';

/**
 * Customers Management Page
 * إدارة العملاء والحدود الائتمانية
 *
 * Protocols Applied (From Father DB):
 * - P-COMPONENT-001: Component Structure
 * - P-STATE-001: Zustand State Management
 * - P-RTL-001: RTL Support
 * - P-DARK-MODE-001: Dark Mode Support
 * - P-SEARCH-001: Search & Filter
 * - P-UX-001: User Experience
 * - P-RESPONSIVE-001: Mobile First
 * - CUST-001: Customer CRUD
 * - CUST-002: Credit Limit Management
 * - CUST-004: Customer Balance Calculation
 */
const Customers = () => {
  const {
    customers,
    loading,
    fetchCustomers,
    searchCustomers,
    getCustomersByStatus,
    getCustomersOverCreditLimit,
    getCustomersNearingCreditLimit,
    getOverdueCustomers,
    getTotalCustomers,
    getActiveCustomers,
    getTotalReceivables,
    error,
    clearError
  } = useCustomerStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showOverCreditOnly, setShowOverCreditOnly] = useState(false);
  const [toast, setToast] = useState({ show: false, type: 'success', message: '' });

  // Load customers on mount
  // fetchCustomers is a stable function from Zustand store
  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get filtered customers
  const getFilteredCustomers = () => {
    if (showOverCreditOnly) {
      return getCustomersOverCreditLimit();
    }

    let filtered = customers;

    if (searchQuery) {
      filtered = searchCustomers(searchQuery);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((c) => c.status === statusFilter);
    }

    return filtered;
  };

  const filteredCustomers = getFilteredCustomers();
  const overCreditCustomers = getCustomersOverCreditLimit();
  const nearingCreditCustomers = getCustomersNearingCreditLimit();
  const overdueCustomers = getOverdueCustomers();

  // Handlers
  const handleAddCustomer = () => {
    setEditingCustomer(null);
    setShowModal(true);
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setShowModal(true);
  };

  const handleCloseModal = async (success = false) => {
    setShowModal(false);

    // Show success toast if customer was saved
    if (success) {
      setToast({
        show: true,
        type: 'success',
        message: editingCustomer ? 'تم تحديث العميل بنجاح! ✓' : 'تم إضافة العميل بنجاح! ✓'
      });
    }

    setEditingCustomer(null);
    // Refresh customers data after modal closes
    await fetchCustomers();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
      {/* Alert Banners */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        {/* Overdue Customers Alert */}
        {overdueCustomers.length > 0 && (
          <div className="bg-orange-50 dark:bg-orange-900/20 border-r-4 border-orange-500 px-4 py-3 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-orange-800 dark:text-orange-300">
                تنبيه: {overdueCustomers.length} عميل متأخر في السداد!
              </p>
              <p className="text-sm text-orange-700 dark:text-orange-400 mt-1">
                يرجى المتابعة مع العملاء المتأخرين
              </p>
            </div>
          </div>
        )}

        {/* Over Credit Limit Alert */}
        {overCreditCustomers.length > 0 && (
          <div className="bg-red-50 dark:bg-red-900/20 border-r-4 border-red-500 px-4 py-3 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-red-800 dark:text-red-300">
                تحذير: {overCreditCustomers.length} عميل تجاوز الحد الائتماني!
              </p>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                يرجى المتابعة والتحصيل من العملاء المتجاوزين
              </p>
            </div>
            <button
              onClick={() => setShowOverCreditOnly(!showOverCreditOnly)}
              className="text-sm text-red-600 dark:text-red-400 hover:underline font-medium"
            >
              {showOverCreditOnly ? 'عرض الكل' : 'عرض المتجاوزين'}
            </button>
          </div>
        )}

        {/* Nearing Credit Limit Alert */}
        {nearingCreditCustomers.length > 0 && !showOverCreditOnly && (
          <div className="bg-orange-50 dark:bg-orange-900/20 border-r-4 border-orange-500 px-4 py-3 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-orange-800 dark:text-orange-300">
                تنبيه: {nearingCreditCustomers.length} عميل قريب من الحد الائتماني
              </p>
              <p className="text-sm text-orange-700 dark:text-orange-400 mt-1">
                استخدم أكثر من 80% من الحد الائتماني
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">العملاء</h1>
          <button
            onClick={handleAddCustomer}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>عميل جديد</span>
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative mb-4">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="ابحث عن عميل (الاسم، الهاتف، الكود...)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pr-10 pl-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
          />
        </div>

        {/* Status Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              statusFilter === 'all'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            الكل ({customers.length})
          </button>
          <button
            onClick={() => setStatusFilter('active')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              statusFilter === 'active'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            نشط ({getCustomersByStatus('active').length})
          </button>
          <button
            onClick={() => setStatusFilter('inactive')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              statusFilter === 'inactive'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            غير نشط ({getCustomersByStatus('inactive').length})
          </button>
          <button
            onClick={() => setStatusFilter('blocked')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              statusFilter === 'blocked'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            محظور ({getCustomersByStatus('blocked').length})
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          {/* Total Customers */}
          <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-primary-600 dark:text-primary-400" />
              <p className="text-xs text-primary-700 dark:text-primary-300">إجمالي العملاء</p>
            </div>
            <p className="text-2xl font-bold text-primary-900 dark:text-primary-100">
              {getTotalCustomers()}
            </p>
          </div>

          {/* Active Customers */}
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
              <p className="text-xs text-green-700 dark:text-green-300">نشط</p>
            </div>
            <p className="text-2xl font-bold text-green-900 dark:text-green-100">
              {getActiveCustomers()}
            </p>
          </div>

          {/* Total Receivables */}
          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              <p className="text-xs text-orange-700 dark:text-orange-300">إجمالي المستحقات</p>
            </div>
            <p className="text-lg font-bold text-orange-900 dark:text-orange-100">
              {getTotalReceivables().toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك
            </p>
          </div>

          {/* Search Results */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Search className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <p className="text-xs text-blue-700 dark:text-blue-300">نتائج البحث</p>
            </div>
            <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {filteredCustomers.length}
            </p>
          </div>
        </div>
      </div>

      {/* Customers List */}
      <div className="p-4">
        {filteredCustomers.length > 0 ? (
          <div className="grid grid-cols-1 gap-3">
            {filteredCustomers.map((customer) => (
              <CustomerCard
                key={customer.id}
                customer={customer}
                onEdit={handleEditCustomer}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 text-center">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {searchQuery || statusFilter !== 'all'
                ? 'لا توجد نتائج'
                : 'لا يوجد عملاء'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {searchQuery
                ? 'جرب البحث بكلمات مختلفة'
                : statusFilter !== 'all'
                ? `لا يوجد عملاء بحالة "${
                    statusFilter === 'active'
                      ? 'نشط'
                      : statusFilter === 'inactive'
                      ? 'غير نشط'
                      : 'محظور'
                  }"`
                : 'ابدأ بإضافة عميل جديد'}
            </p>
            {!searchQuery && statusFilter === 'all' && (
              <button
                onClick={handleAddCustomer}
                className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg inline-flex items-center gap-2 transition-colors"
              >
                <Plus className="w-5 h-5" />
                إضافة عميل جديد
              </button>
            )}
          </div>
        )}
      </div>

      {/* Customer Modal */}
      {showModal && (
        <CustomerModal
          customer={editingCustomer}
          onClose={handleCloseModal}
        />
      )}

      {/* Error Display */}
      {error && (
        <div className="fixed bottom-24 left-4 right-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3 shadow-lg">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-red-800 dark:text-red-300">خطأ</p>
            <p className="text-sm text-red-700 dark:text-red-400 mt-1">{error}</p>
          </div>
          <button
            onClick={clearError}
            className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
          >
            ✕
          </button>
        </div>
      )}

      {/* Success Toast */}
      {toast.show && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast({ ...toast, show: false })}
          duration={3000}
        />
      )}
    </div>
  );
};

export default Customers;
